/**
 * The wormhole: what enters, what leaves, and what is still inside.
 *
 * This chain's transfers are shielded. A transfer inserts a commitment as a
 * **note**; spending one proves a **nullifier**, which the runtime records so it
 * cannot be spent twice. Notes created minus notes spent is what has not yet
 * moved — and every one of those, when it does move, will name a recipient and
 * an amount and **not a sender**.
 *
 * That last fact is why this page exists. The distribution table shipped with an
 * outflow column reading zero for every miner, which looked like evidence that
 * nothing had left those addresses. It was not: it was the chain's privacy doing
 * its job, and the observer had nothing anywhere that said so. This page is the
 * honest account of how much of the chain is unobservable and by what route.
 */

import { useEffect, useState } from 'react'

import type { WormholeSummary } from '../api/generated/WormholeSummary'
import { FlowChart } from './FlowChart'
import { RequestFailed, fetchWormhole } from '../api/rest'
import { height as fmtHeight, tokens } from '../lib/format'
import { Field } from './Field'

/** The route's own name, since block initialisation has no extrinsic. */
function routeName(produced_by: string): string {
  return produced_by === '' ? 'block initialisation' : produced_by
}

/** What a route actually is, in a few words, because `Utility::batch_all` does
 *  not tell a reader whether their money can be traced. */
function routeMeaning(produced_by: string, hidden: boolean): string {
  if (produced_by === '')
    return hidden ? 'mining rewards, paid by the chain' : 'block initialisation'
  if (produced_by.startsWith('Wormhole::')) return 'a shielded spend — no sender recorded'
  return hidden ? 'no sender recorded' : 'an ordinary transfer, sender recorded'
}

/**
 * What to call each cohort class, and what it means.
 *
 * The identifiers come from the API and are stable; the wording is this
 * page's. Kept as one table so the two machinery classes cannot drift apart
 * from the explanation that stops them being read as payouts.
 */
const COHORTS: Record<string, { label: string; meaning: string }> = {
  infrastructure: {
    label: 'Aggregator rebates',
    meaning: 'credited in more than a fifth of every batch anyone submitted',
  },
  miner_fee: {
    label: 'Miner fee shares',
    meaning: 'paid to whichever block author included the batch',
  },
  paid_once: { label: 'Paid once', meaning: 'a single credit, ever' },
  paid_a_few: { label: 'Paid 2–4 times', meaning: '' },
  paid_often: { label: 'Paid 5–19 times', meaning: '' },
  paid_regularly: {
    label: 'Paid 20+ times',
    meaning: 'a recurring payee — the closest thing to a payroll the chain shows',
  },
}

/** Whether a class is the protocol's own plumbing rather than somebody's payee. */
function isMachinery(kind: string): boolean {
  return kind === 'infrastructure' || kind === 'miner_fee'
}

export function WormholePanel({
  chain,
  decimals,
  symbol,
}: {
  chain: string
  decimals: number
  symbol: string
}) {
  const [data, setData] = useState<WormholeSummary | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setData(null)
    setError(null)
    fetchWormhole(chain, controller.signal)
      .then(setData)
      .catch((e: unknown) => {
        if (controller.signal.aborted) return
        setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
      })
    return () => controller.abort()
  }, [chain])

  if (error) return <p className="empty">{error}</p>
  if (!data) return <p className="empty">Counting notes…</p>

  const outstanding =
    data.notes_created !== null && data.notes_spent !== null
      ? data.notes_created - data.notes_spent
      : null

  const hiddenTransfers = data.routes
    .filter((r) => r.sender_hidden && r.produced_by !== '')
    .reduce((sum, r) => sum + r.transfers, 0)
  const visibleTransfers = data.routes
    .filter((r) => !r.sender_hidden)
    .reduce((sum, r) => sum + r.transfers, 0)

  // What share of everything the exits pay is the protocol paying itself. The
  // headline of the cohort table, because without it the bands below read as a
  // pool ledger. BigInt throughout: these are token amounts, and a Number would
  // lose precision well before the total does.
  const machineryShare = (() => {
    let total = 0n
    let machinery = 0n
    for (const c of data.cohorts) {
      let amount: bigint
      try {
        amount = BigInt(c.amount)
      } catch {
        return null
      }
      total += amount
      if (isMachinery(c.kind)) machinery += amount
    }
    if (total === 0n) return null
    return Number((machinery * 1000n) / total) / 10
  })()

  // Notes still inside, as a share of every note ever created. A whole number:
  // the figure is a headline, and a decimal place on it would imply a precision
  // the ceiling-limited nullifier count cannot promise.
  const unspentShare =
    outstanding !== null && data.notes_created !== null && data.notes_created > 0
      ? Math.round((outstanding / data.notes_created) * 100)
      : null

  // Mean credit, computed from the summed amount rather than sent per-row: the
  // difference between the machinery's average and a payee's is most of what
  // separates them, and rounding it away would flatten the table.
  const each = (amount: string, credits: number): string => {
    if (credits === 0) return '—'
    let total: bigint
    try {
      total = BigInt(amount)
    } catch {
      return '—'
    }
    return tokens((total / BigInt(credits)).toString(), decimals, 3)
  }

  return (
    <>
      <section className="panel" style={{ marginBottom: 26 }}>
        <div className="panel-head">
          <h2 className="panel-title">The wormhole</h2>
          <span className="eyebrow">read from chain state</span>
        </div>
        <div className="node-grid">
          <Field
            label="Notes created"
            value={data.notes_created === null ? '—' : fmtHeight(data.notes_created)}
            note="one per transfer, ever"
          />
          <Field
            label="Notes spent"
            value={data.notes_spent === null ? '—' : fmtHeight(data.notes_spent)}
            note={
              data.notes_spent_complete ? 'nullifiers, counted in full' : 'count hit its ceiling'
            }
          />
          <Field
            label="Outstanding"
            value={outstanding === null ? '—' : fmtHeight(outstanding)}
            note="unspent — still inside"
          />
          <Field
            label="Tree depth"
            value={data.tree_depth === null ? '—' : String(data.tree_depth)}
          />
          <Field
            label="Proofs rejected"
            value={fmtHeight(data.segments_denied)}
            note="segments denied"
          />
          <Field label="Exits that failed to mint" value={fmtHeight(data.exit_mint_failed)} />
        </div>
        <p className="panel-note">
          A transfer on this chain inserts a commitment — a <em>note</em> — and spending one proves
          a <em>nullifier</em>, which the runtime records so the same note cannot be spent twice.
          Notes created less notes spent is what has not yet moved.{' '}
          <strong>
            Every one of those, when it moves, will record a recipient and an amount and not a
            sender.
          </strong>{' '}
          A zero under proofs rejected is a finding rather than a gap: it says no proof has ever
          been refused, which is a different statement from nobody having tried.
        </p>
        {unspentShare !== null && (
          <>
            {/* A single ratio against a limit, so a meter rather than a chart:
                one bar, one hue, the track being the same hue at wash weight.
                A two-slice pie of the same fact would be the anti-pattern. */}
            <div
              className="meter"
              role="img"
              aria-label={`${unspentShare}% of every note ever created is still unspent`}
            >
              <div className="meter-fill" style={{ width: `${unspentShare}%` }} />
            </div>
            <p className="panel-note">
              <strong>{unspentShare}% of every note ever created is still unspent.</strong> That is
              the standing claim on this chain&apos;s privacy: value already committed, whose
              movement — when it comes — will name a recipient and no sender.
            </p>
          </>
        )}
      </section>

      <section className="panel" style={{ marginBottom: 26 }}>
        <div className="panel-head">
          <h2 className="panel-title">What goes in, what comes out</h2>
          <span className="eyebrow">{symbol} per day · this observer&apos;s index</span>
        </div>
        <FlowChart days={data.days} decimals={decimals} symbol={symbol} />
        <p className="panel-note">
          Bars above the rule arrived in the pool; bars below it left. There is no second colour
          because this site has one data hue, and the two-shade alternative was measured rather than
          assumed — the pair scores a normal-vision separation well under the floor, so direction is
          carried by <em>side of the rule</em>, which no reader can fail to see.
        </p>
        <p className="panel-note">
          <strong>Direction is read from the extrinsic that produced each transfer</strong>, never
          from the sender, which is the same sentinel whichever way the value is going. Anything
          settled by <code>verify_public_batch</code> or <code>verify_private_batch</code> is value
          leaving; everything else — an ordinary transfer, a mining reward at block initialisation —
          is value arriving.
        </p>
        <p className="panel-note">
          Two things stop this being a closed ledger, and both are worth holding in mind. An exit{' '}
          <em>also</em> inserts a fresh note for whoever it credits, so value that leaves is
          immediately committed again — the pool is not drained by its own outflow. And the inbound
          side is dominated by the chain paying its miners rather than by anyone choosing to
          deposit, because every transfer on this chain is recorded as a note whether its owner
          wanted privacy or not.
        </p>
      </section>

      <section className="panel" style={{ marginBottom: 26 }}>
        <div className="panel-head">
          <h2 className="panel-title">How value moves</h2>
          <span className="eyebrow">
            amounts in {symbol} ·{' '}
            {data.indexed_from !== null && data.indexed_to !== null
              ? `blocks ${fmtHeight(data.indexed_from)}–${fmtHeight(data.indexed_to)}`
              : 'nothing indexed yet'}
          </span>
        </div>
        <div className="scroll-x">
          <table className="board">
            <caption className="visually-hidden">
              Every transfer route, and whether the chain recorded who sent it
            </caption>
            <thead>
              <tr>
                <th scope="col" className="left">
                  Produced by
                </th>
                <th scope="col" className="left">
                  Sender
                </th>
                <th scope="col">Transfers</th>
                <th scope="col">Recipients</th>
                <th scope="col">Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.routes.map((r) => (
                <tr key={`${r.produced_by}-${String(r.sender_hidden)}`}>
                  <td className="left">
                    <div className="call-cell">
                      <code>{routeName(r.produced_by)}</code>
                      <span className="runtime-doc">
                        {routeMeaning(r.produced_by, r.sender_hidden)}
                      </span>
                    </div>
                  </td>
                  <td className="left">
                    {r.sender_hidden ? (
                      <span className="chip chip-failed">hidden</span>
                    ) : (
                      <span className="runtime-modifier">recorded</span>
                    )}
                  </td>
                  <td className="numeral">{fmtHeight(r.transfers)}</td>
                  <td className="numeral">{fmtHeight(r.recipients)}</td>
                  <td className="numeral">{tokens(r.amount, decimals, 1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="panel-note">
          <strong>
            {fmtHeight(hiddenTransfers)} transfers carried no sender, against{' '}
            {fmtHeight(visibleTransfers)} that did.
          </strong>{' '}
          The hidden ones produced by <code>Wormhole::verify_public_batch</code> and{' '}
          <code>Wormhole::verify_private_batch</code> are real payments whose origin the chain does
          not write down — this is the privacy feature, not a gap in the index. Hidden transfers
          from <em>block initialisation</em> are something else: they are the chain paying miners,
          and their sender is the runtime rather than anybody.
        </p>
        <p className="panel-note">
          This is the reason the distribution table can only speak about outflow it can see. An
          address that has paid out through the wormhole shows nothing there, and that is the chain
          behaving correctly rather than the address behaving suspiciously.
        </p>
      </section>

      <section className="panel" style={{ marginBottom: 26 }}>
        <div className="panel-head">
          <h2 className="panel-title">Who the exits pay</h2>
          <span className="eyebrow">
            public batches only · amounts in {symbol}
            {machineryShare !== null && ` · ${machineryShare}% is plumbing`}
          </span>
        </div>
        <div className="scroll-x">
          <table className="board">
            <caption className="visually-hidden">
              Credits out of the wormhole, grouped by what each address appears to be
            </caption>
            <thead>
              <tr>
                <th scope="col" className="left">
                  Who
                </th>
                <th scope="col">Addresses</th>
                <th scope="col">Credits</th>
                <th scope="col">Amount</th>
                <th scope="col">Each</th>
              </tr>
            </thead>
            <tbody>
              {data.cohorts.map((c) => {
                const meta = COHORTS[c.kind]
                return (
                  <tr key={c.kind}>
                    <td className="left">
                      <div className="call-cell">
                        <span>{meta?.label ?? c.kind}</span>
                        {meta?.meaning ? <span className="runtime-doc">{meta.meaning}</span> : null}
                      </div>
                    </td>
                    <td className="numeral">{fmtHeight(c.addresses)}</td>
                    <td className="numeral">{fmtHeight(c.credits)}</td>
                    <td className="numeral">{tokens(c.amount, decimals, 1)}</td>
                    <td className="numeral">{each(c.amount, c.credits)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <p className="panel-note">
          <strong>
            Most of what leaves the wormhole is the protocol paying itself
            {machineryShare !== null && `, ${machineryShare}% of it`}.
          </strong>{' '}
          An aggregator&apos;s rebate is minted on every bundle it submits, so a handful of
          addresses appear in almost every batch anyone sent — one of them in 91.5% of them, and in
          none of the private ones. A miner&apos;s fee share goes to whoever included the batch,
          which is why a mining address can be credited hundreds of times for a few hundredths of a{' '}
          {symbol} each. Neither is anybody&apos;s payout.
        </p>
        <p className="panel-note">
          Batch <em>size</em> is deliberately not on this page. A public batch carries one segment
          per inner private batch and a segment is one client, so a 120-output batch is an
          aggregator bundling unrelated people — it measures aggregator throughput, and reading it
          as one payer paying a hundred payees is the mistake this table exists to prevent.
        </p>
        <p className="panel-note">
          What survives the subtraction is the bottom rows, and they are the real finding: a small
          number of addresses paid over and over, taking most of the remaining value at a larger
          amount each time.{' '}
          <strong>That is a recurring-payment shape, and no more than that.</strong> The chain
          records no sender, so nothing here names a payer — and a regular payee could as easily be
          an exchange deposit, or one person moving their own funds on a schedule, as a pool meeting
          its obligations.
        </p>
      </section>
    </>
  )
}
